<template>
  <div v-if="day_report">
    <v-card variant="outlined" class="content-card">
      <v-card-title class="bg-primary-lighten-5 d-flex align-center">
        <v-avatar
          class="mr-3"
          :color="
            day_report.submitted ? 'success-lighten-4' : 'warning-lighten-4'
          "
          size="42"
        >
          <v-icon
            :icon="
              day_report.submitted ? 'mdi-check-bold' : 'mdi-progress-clock'
            "
            :color="day_report.submitted ? 'success' : 'warning'"
          ></v-icon>
        </v-avatar>
        <div>
          <div class="text-h6">Verslag van {{ getUserName }}</div>
          <div class="text-subtitle-2 text-medium-emphasis">
            <v-chip
              size="small"
              :color="
                day_report.day_report_type
                  ? 'primary-lighten-3'
                  : 'grey-lighten-2'
              "
              class="mr-2"
              variant="tonal"
            >
              {{ day_report.day_report_type || "Geen type" }}
            </v-chip>
            {{ formatDate(day_report.date) }}
          </div>
        </div>

        <v-spacer></v-spacer>

        <DayReportOptions v-if="showOptions" :day_report="day_report" />
      </v-card-title>

      <v-card-text class="pt-4">
        <!-- Day Report Lines Section -->
        <div class="d-flex align-center mb-3">
          <v-icon color="primary-darken-1" class="mr-2"
            >mdi-format-list-bulleted</v-icon
          >
          <span class="text-subtitle-1">Veldwerk</span>
        </div>

        <div v-intersect="onTopIntersect"></div>

        <QuickReport
          v-if="
            !isIntersecting &&
            isTopIntersecting &&
            !day_report.submitted &&
            canEdit
          "
          v-model:item="new_day_report_line"
          :icons="day_report_line_icons"
          category_field="line_category"
          content_field="content"
          class="mb-4"
          @click="createLine"
        />

        <DayReportLineList
          class="mb-4 day-report-line-list"
          :day_report_lines="day_report_lines"
          :icons="day_report_line_icons"
          :current_active="current_active"
        />

        <QuickReport
          v-if="!day_report.submitted && canEdit"
          v-model:item="new_day_report_line"
          v-intersect="onIntersect"
          :icons="day_report_line_icons"
          category_field="line_category"
          content_field="content"
          @click="createLine"
        />
      </v-card-text>
    </v-card>
  </div>
  <div v-else class="loading-state">
    <v-card variant="outlined" class="content-card">
      <v-card-text class="py-6">
        <div class="d-flex flex-column align-center">
          <v-progress-circular
            indeterminate
            color="primary"
            class="mb-4"
          ></v-progress-circular>
          <div class="text-subtitle-1 text-medium-emphasis">
            Verslag laden...
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import DayReportLineList from "../day_report_lines/DayReportLineList.vue";
import QuickReport from "../ui/QuickReport.vue";
import DayReportOptions from "./DayReportOptions.vue";
import { useDayReportLinesStoreRefactor } from "@/stores/dayReportLinesStoreRefactor";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import moment from "moment";

// Props with more flexibility
const props = defineProps({
  day_report: {
    type: Object,
    required: false,
    default: null,
  },
  current_active: {
    type: Boolean,
    default: false,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
  showOptions: {
    type: Boolean,
    default: true,
  },
});

// Store instances
const dayReportLinesStore = useDayReportLinesStoreRefactor();
const usersStore = useUsersStoreRefactor();

// Reactive state
const isIntersecting = ref(false);
const isTopIntersecting = ref(false);
const new_day_report_line = ref({
  line_category: "administration",
  content: "",
});

// Computed properties
const day_report_line_icons = computed(() => dayReportLinesStore.icons);
const day_report_lines = computed(() => {
  if (!props.day_report?.id) {
    console.log("No day report ID available, returning empty array");
    return [];
  }
  const lines = dayReportLinesStore.getLinesByDayReportId(props.day_report.id);
  console.log(
    `DayReportContent: Retrieved ${lines.length} lines for day report ${props.day_report.id}`,
  );
  return lines;
});

// Safe getter for user name
const getUserName = computed(() => {
  if (!props.day_report?.user_id) return "Onbekende gebruiker";

  // Get user from users store
  const user = usersStore.getUserById(props.day_report.user_id);
  if (!user) return "Onbekende gebruiker";

  if (user.first_name || user.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }

  return user.name || "Onbekende gebruiker";
});

// Methods
function formatDate(date) {
  if (!date) return "Geen datum";
  return moment(date).format("DD-MM-YYYY");
}

function onIntersect(entries) {
  if (entries && entries.length > 0) {
    isIntersecting.value = entries[0].isIntersecting;
  }
}

function onTopIntersect(entries) {
  if (entries && entries.length > 0) {
    isTopIntersecting.value = entries[0].isIntersecting;
  }
}

async function createLine() {
  try {
    new_day_report_line.value.day_report_id = props.day_report.id;
    await dayReportLinesStore.create({ ...new_day_report_line.value });

    // Reset the form
    new_day_report_line.value = {
      line_category: "administration",
      content: "",
    };
  } catch (error) {
    console.error("Error creating day report line:", error);
  }
}

// Lifecycle hooks
onMounted(async () => {
  console.log("DayReportContent mounted, day_report:", props.day_report);
  // Ensure users are loaded
  if (usersStore.users.length === 0) {
    await usersStore.getAll();
  }
  dayReportLinesStore.getForDayReport(props.day_report.id, true);

  // Don't load day_report_lines here since the watcher with immediate: true will handle it
  console.log(
    "DayReportContent: Skipping day_report_lines load in onMounted - watcher will handle it",
  );
});

// Consolidated function to load day report lines with caching
async function loadDayReportLines(dayReportId) {
  if (!dayReportId) {
    console.log("No day report ID provided, skipping line fetch");
    return;
  }

  // Check if we already have lines for this day report to avoid redundant calls
  const existingLines = dayReportLinesStore.getLinesByDayReportId(dayReportId);

  // Use the baseStore's shouldRefetch logic - if data is fresh, don't refetch
  if (existingLines.length > 0 && !dayReportLinesStore.shouldRefetch) {
    console.log(
      `DayReportContent: Already have ${existingLines.length} fresh lines for day report ${dayReportId}, skipping API call`,
    );
    return;
  }

  console.log(
    `DayReportContent: Loading day report lines for day report ${dayReportId}`,
  );
  await dayReportLinesStore.getForDayReport(dayReportId);
}

// Watch for changes in day_report.id and reload day report lines if needed
watch(
  () => props.day_report?.id,
  async (newId, oldId) => {
    console.log(
      "DayReportContent: Day report ID changed from",
      oldId,
      "to",
      newId,
    );
    if (newId && newId !== oldId) {
      console.log(
        "DayReportContent: Loading day report lines for day report ID:",
        newId,
      );
      loadDayReportLines(newId);
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.content-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.v-card-title {
  padding: 16px 20px;
  font-size: 1.25rem;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.87);
}

.v-card-text {
  padding: 16px 20px;
}

/* Mobile view optimizations */
@media (max-width: 600px) {
  .v-card-title {
    padding: 12px 16px;
    font-size: 1.1rem;
  }

  .v-card-text {
    padding: 12px 16px;
  }

  .content-card {
    border-radius: 4px;
  }
}

.day-report-line-list :deep(.v-expansion-panels) {
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.day-report-line-list :deep(.v-expansion-panel) {
  border: none;
  box-shadow: none;
  margin-bottom: 4px;
}

.day-report-line-list :deep(.v-expansion-panel-title) {
  padding: 12px 16px;
  min-height: auto;
  background-color: rgba(0, 0, 0, 0.02);
}

.day-report-line-list :deep(.v-expansion-panel-text__wrapper) {
  padding: 0;
}
</style>
